import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

export interface SimulateRateData {
  flowLabel: string;           // 'Retiro' | 'Transferencia Vita' | 'Payin'
  countryCode: string;
  destLabel: string;
  flagIso: string | null;
  sourceCurrency: string;
  destCurrency: string;
  sellPrice: number;
  fixedCost: number;
  fixedCostUsd: number;
  // Opcionales — solo se muestran si vienen
  minAmount?: number;
  businessDays?: number;
}

@Component({
  selector: 'app-simulate-rate-dialog',
  templateUrl: './simulate-rate-dialog.component.html',
  styleUrls: ['./simulate-rate-dialog.component.sass'],
})
export class SimulateRateDialogComponent {
  amount: number;

  /**
   * Tasa Vita editable en vivo. Inicia en `data.sellPrice` (lo que vino del
   * payload de Vita). El admin puede simular escenarios con tasas distintas
   * — útil para evaluar P&L si la tasa cambia o para ajustar negociaciones.
   * NO pega al backend: solo recalcula el simulador.
   */
  vitaRate: number;

  /** Tasa original (snapshot) para detectar modificaciones y poder restablecer. */
  readonly originalVitaRate: number;

  /**
   * Markup Aguacate editable en vivo. Default 1.5% — el user puede subirlo
   * o bajarlo (incluyendo negativo) con el input para ver el profit
   * resultante. NO pega al backend: todo se recomputa en el getter sobre
   * `vitaRate` + `amount`.
   *
   * Markup negativo = Aguacate da mejor tasa que Vita (subsidio / incentivo
   * comercial). El profit se vuelve negativo (pérdida).
   *
   * Modelo contable: la tasa Vita queda intacta; la comisión Aguacate es
   * una línea aparte sobre el bruto de la conversión (ver memoria
   * `project_aguacate_vita_markup_model`).
   */
  markupPct = 1.5;

  constructor(
    public dialogRef: MatDialogRef<SimulateRateDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: SimulateRateData,
  ) {
    this.amount = this.defaultAmount(data.sourceCurrency);
    this.vitaRate = data.sellPrice || 0;
    this.originalVitaRate = data.sellPrice || 0;
  }

  private defaultAmount(source: string): number {
    const c = (source || '').toLowerCase();
    if (c === 'usd' || c === 'eur' || c === 'gbp') return 100;
    return 100000;
  }

  // ─── Caso 1: Con Vita directo ───

  /** Bruto antes de comisiones: amount × vitaRate (moneda destino). */
  get grossDest(): number {
    return (this.amount || 0) * (this.vitaRate || 0);
  }

  /** Lo que recibe el beneficiario con Vita directo: bruto − comisión fija Vita. */
  get receive(): number {
    const v = this.grossDest - (this.data.fixedCost || 0);
    return v > 0 ? v : 0;
  }

  // ─── Caso 2: Con Vita + Aguacate ───

  /**
   * Tasa efectiva que Aguacate ofrece al usuario, derivada de la tasa Vita.
   * Markup positivo → Aguacate cobra spread → tasa Aguacate < tasa Vita.
   * Markup negativo → Aguacate subsidia → tasa Aguacate > tasa Vita.
   */
  get aguacateRate(): number {
    return (this.vitaRate || 0) * (1 - (this.markupPct || 0) / 100);
  }

  /**
   * Comisión Aguacate expresada en moneda destino. Se calcula sobre el
   * bruto de la conversión. Puede ser negativa si markupPct es negativo
   * (significa que Aguacate paga al usuario un extra sobre la tasa Vita).
   */
  get aguacateCommissionDest(): number {
    if (!this.markupPct) return 0;
    return this.grossDest * (this.markupPct / 100);
  }

  /** Comisión Aguacate expresada en moneda origen. Puede ser negativa. */
  get aguacateCommissionSrc(): number {
    if (!this.markupPct) return 0;
    return (this.amount || 0) * (this.markupPct / 100);
  }

  /** Lo que recibe con Vita+Aguacate: bruto − com. Vita − com. Aguacate. */
  get aguacateReceive(): number {
    const v = this.receive - this.aguacateCommissionDest;
    return v > 0 ? v : 0;
  }

  /** Profit Aguacate en moneda origen — indicador P&L. Puede ser negativo. */
  get aguacateProfitUsd(): number {
    if (!this.vitaRate) return 0;
    return this.aguacateCommissionDest / this.vitaRate;
  }

  /** Bruto en destino con tasa Aguacate (antes de fee Vita). */
  get grossAguacateDest(): number {
    return (this.amount || 0) * this.aguacateRate;
  }

  // ─── Validaciones y secundarios ───

  get belowMin(): boolean {
    if (!this.data.minAmount) return false;
    return (this.amount || 0) < this.data.minAmount;
  }

  /**
   * Markup admite negativo (subsidio). Bound práctico: -100..100 — fuera
   * de eso es absurdo (>100% sería comerse más que el bruto, <-100% sería
   * pagar más del doble de la tasa Vita).
   */
  get markupInvalid(): boolean {
    return this.markupPct < -100 || this.markupPct > 100;
  }

  /** Vita rate debe ser positiva — 0 o negativa rompe toda la simulación. */
  get vitaRateInvalid(): boolean {
    return !this.vitaRate || this.vitaRate <= 0;
  }

  /** Indica si la tasa Vita fue editada respecto al valor original. */
  get vitaRateModified(): boolean {
    return Math.abs((this.vitaRate || 0) - this.originalVitaRate) > 1e-9;
  }

  resetVitaRate(): void {
    this.vitaRate = this.originalVitaRate;
  }

  close(): void {
    this.dialogRef.close();
  }
}
